// src/components/stripe/StableStripeProvider.jsx - BULLETPROOF SINGLETON
import React, { createContext, useContext, useRef, useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '../../services/stripeService';

// Global singleton to prevent multiple instances
let globalStripeInstance = null;
let globalStripeOptions = null;

// Create context for stable Stripe access
const StableStripeContext = createContext({});

// Hook to access stable Stripe context
export const useStableStripe = () => useContext(StableStripeContext);

// Singleton provider that NEVER unmounts children during payment
const StableStripeProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const providersInitialized = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Initialize Stripe instance only once globally
  useEffect(() => {
    if (providersInitialized.current) return;
    
    const initializeStripe = async () => {
      try {
        console.log('[StableStripeProvider] Initializing global Stripe instance...');
        
        if (!globalStripeInstance) {
          globalStripeInstance = await getStripe();
        }
        
        if (!globalStripeOptions) {
          globalStripeOptions = {
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#6366f1',
                colorBackground: '#ffffff',
                colorText: '#1f2937',
                colorDanger: '#ef4444',
                fontFamily: 'system-ui, sans-serif',
                spacingUnit: '4px',
                borderRadius: '8px',
                fontSizeBase: '16px',
              },
              rules: {
                '.Card': {
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '16px',
                },
                '.Card:focus': {
                  borderColor: '#6366f1',
                  boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
                },
                '.Card--invalid': {
                  borderColor: '#ef4444',
                },
              },
            },
            locale: 'fr',
            fonts: [],
          };
        }
        
        if (mountedRef.current) {
          setIsReady(true);
          providersInitialized.current = true;
          console.log('[StableStripeProvider] Global Stripe instance ready');
        }
      } catch (err) {
        console.error('[StableStripeProvider] Initialization failed:', err);
        if (mountedRef.current) {
          setError(err.message);
        }
      }
    };

    initializeStripe();
  }, []);

  // Loading state
  if (!isReady && !error) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
        <div className="flex flex-col items-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="text-gray-600 text-sm">Chargement sécurisé...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 text-center bg-red-50 border border-red-200 rounded-lg">
        <div className="text-red-600">
          <svg className="w-10 h-10 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.064 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="font-semibold mb-2">Erreur de paiement</h3>
          <p className="text-sm mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }

  // Stable Elements provider with context
  return (
    <Elements 
      stripe={globalStripeInstance} 
      options={globalStripeOptions}
      key="global-stable-stripe-elements" // Never changes
    >
      <StableStripeContext.Provider value={{ 
        isReady: true,
        stripeInstance: globalStripeInstance,
        options: globalStripeOptions 
      }}>
        {children}
      </StableStripeContext.Provider>
    </Elements>
  );
};

export default StableStripeProvider;